/**
 * `ExternalCvCompetenceDetail` — dettaglio di una competenza per un profilo CV
 * esterno, in **sola lettura**.
 *
 * Drill-down dal dettaglio arricchito del profilo esterno (`ExternalCvDetail`):
 * mostra le skill della competenza con i voti derivati dalle valutazioni AI del
 * profilo (Livello AI, Esperienza, Score) e il donut di Copertura in
 * intestazione. Rispecchia `TeamMemberCompetenceDetail`, ma per un profilo
 * esterno omette le colonne Propensione e Reviews: sarebbero sempre vuote (un
 * profilo esterno non ha propensione né recensioni — quei campi sono `null`
 * negli assessment esterni). L'altra differenza sostanziale è la colonna
 * "Livello", che per un profilo esterno legge il Livello AI (`aiLevel`, unica
 * Fonte di Livello) anziché l'autovalutazione (`livelloSelf`).
 *
 * Dati:
 *  - `GET /api/external-cv/profiles/:id/dashboard` → `ExternalCvDashboardDto`:
 *    da qui gli `assessments` (`AssessmentView[]`) del profilo e il
 *    `CompetencyMatch` con `competenceId` uguale a quello richiesto (per il
 *    donut di Copertura e la Proficiency in intestazione). `retry: false`; il
 *    `404` (profilo inesistente) e il `403` (accesso negato) sono gestiti con un
 *    messaggio dedicato, coerentemente con `ExternalCvDetail`.
 *  - `GET /api/competences` → risolve la competenza dall'`:competenceId` e il
 *    suo elenco di `skillIds`/`skillWeights`/`description`/`mission`/`name`;
 *  - `GET /api/skills` → nomi e descrizioni delle skill (anche quelle non ancora
 *    valutate).
 *
 * L'accesso è aperto a ogni ruolo autenticato con Levels_Access (come il resto
 * della sezione "CV esterni"); l'API resta l'autorità autorevole.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type {
  AssessmentView,
  CompetenceDto,
  CompetencyMatch,
  ExternalCvDashboardDto,
  ScoringConfigDto,
  SkillDto,
} from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { ApiRequestError } from '../api/client';
import { ABSENT_INDICATOR } from '../components/ScoreTriple';
import { ScoreBadge } from '../components/ScoreBadge';
import { CertificationCountBadge } from '../components/CertificationCountBadge';
import { ScoreCalcGuide } from '../components/ScoreCalcGuide';
import { ScoreDetailModal } from '../components/ScoreDetailModal';
import { ScoreGuide } from '../components/ScoreGuide';
import { LivelloBadge } from '../components/LivelloBadge';
import { LivelloGuide } from '../components/LivelloGuide';
import { scoreColor } from '../components/StarRating';
import {
  Alert,
  Button,
  Card,
  DataTable,
  DotRating,
  EmptyState,
  InfoHint,
  Loading,
  PageHeader,
  type Column,
  type SortDirection,
} from '../components/ui';
import type { ExperienceRange } from '../shared/dto';
import {
  DEFAULT_SCORING_CONFIG,
  DEFAULT_SKILL_WEIGHT,
  EXPERIENCE_RANGE_LABELS,
  EXPERIENCE_RANGE_VALUES,
  MAX_SKILL_WEIGHT,
  livelloRank,
} from '../shared/dto';

/** Etichetta descrittiva del peso, allineata a `SkillWeightEditor`. */
const WEIGHT_HINT: Record<number, string> = {
  1: 'Marginale',
  2: 'Bassa',
  3: 'Media',
  4: 'Alta',
  5: 'Essenziale',
};

/** Formatta una percentuale `0..100` come intero con `%`. */
function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}

/** Raggio del cerchio del donut (viewBox 36×36, stroke 4 → centro a 18). */
const DONUT_RADIUS = 16;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

/**
 * `ScoreDonut` — anello di progresso di una percentuale `0..100`, identico a
 * quello della `CompetencyMatchCard` e della dashboard personale. Qui
 * rappresenta la **Copertura** della competenza; `label` personalizza il testo
 * accessibile.
 */
function ScoreDonut({ score, label }: { score: number; label: string }) {
  const clamped = Math.min(100, Math.max(0, score));
  const rounded = Math.round(clamped);
  const ringColor = scoreColor(clamped / 10) ?? 'var(--color-primary)';
  const dash = (clamped / 100) * DONUT_CIRCUMFERENCE;

  return (
    <div
      className="competency-match-card__donut"
      role="img"
      aria-label={`${label} ${rounded} su 100`}
      title={`${label} ${rounded}/100`}
    >
      <svg viewBox="0 0 36 36" className="competency-match-card__donut-svg" aria-hidden="true">
        <circle className="competency-match-card__donut-track" cx="18" cy="18" r={DONUT_RADIUS} />
        <circle
          className="competency-match-card__donut-fill"
          cx="18"
          cy="18"
          r={DONUT_RADIUS}
          stroke={ringColor}
          strokeDasharray={`${dash} ${DONUT_CIRCUMFERENCE - dash}`}
          transform="rotate(-90 18 18)"
        />
      </svg>
      <span className="competency-match-card__donut-value" aria-hidden="true">
        {rounded}
      </span>
    </div>
  );
}

/** Confronto lessicografico deterministico dei nomi skill (già orientato). */
function compareSkillName(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const sign = direction === 'desc' ? -1 : 1;
  if (a.skillName < b.skillName) return -sign;
  if (a.skillName > b.skillName) return sign;
  return 0;
}

/**
 * Comparatore per la colonna "Livello": ordina per rango della scala
 * (`e-1..e-5`), con i livelli assenti (`null`) sempre in coda. Per un profilo
 * esterno il Livello è quello AI (`aiLevel`), unica Fonte di Livello.
 */
function compareAiLevel(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const la = a.aiLevel;
  const lb = b.aiLevel;
  if (la === null && lb === null) return 0;
  if (la === null) return 1;
  if (lb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return sign * (livelloRank(la) - livelloRank(lb));
}

/** Comparatore per la colonna "Esperienza": rango della scala di fasce, `null` in coda. */
function compareExperience(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const ra = a.yearsOfExperience;
  const rb = b.yearsOfExperience;
  if (ra === null && rb === null) return 0;
  if (ra === null) return 1;
  if (rb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return (
    sign *
    (EXPERIENCE_RANGE_VALUES.indexOf(ra) - EXPERIENCE_RANGE_VALUES.indexOf(rb))
  );
}

/** Comparatore per la colonna "Score": Score_Skill derivato dall'API, `null` in coda. */
function compareScore(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const va = a.score;
  const vb = b.score;
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return sign * (va - vb);
}

/** Rende la fascia di anni di esperienza dichiarata, o "—" se assente. */
function experienceCell(value: ExperienceRange | null) {
  if (value === null) {
    return (
      <span className="skill-list__absent" aria-label="Esperienza: non dichiarata">
        {ABSENT_INDICATOR}
      </span>
    );
  }
  return <span className="skill-list__years">{EXPERIENCE_RANGE_LABELS[value]}</span>;
}

/**
 * Rende lo "Score" (Score_Skill derivato) come badge colorato, o "—" se assente,
 * affiancato dal badge del numero di certificazioni valide che vi contribuiscono
 * (sempre `0` per un profilo esterno).
 */
function scoreBadgeCell(
  row: AssessmentView,
  onScoreClick?: (row: AssessmentView) => void,
) {
  const certBadge = (
    <CertificationCountBadge count={row.certificationCount} skillName={row.skillName} />
  );
  if (row.score === null) {
    return (
      <span className="skill-list__score-cell">
        <span className="skill-list__absent" aria-label="Score: assente">
          {ABSENT_INDICATOR}
        </span>
        {certBadge}
      </span>
    );
  }
  // Con un handler (vista interna) lo Score è cliccabile e apre il dettaglio del
  // calcolo; senza, resta un badge statico.
  return (
    <span className="skill-list__score-cell">
      {onScoreClick === undefined ? (
        <ScoreBadge value={row.score} label="Score" />
      ) : (
        <button
          type="button"
          className="score-badge-button"
          onClick={() => onScoreClick(row)}
          aria-label={`Score ${row.score.toFixed(1)} su 10 — mostra il dettaglio del calcolo`}
          title="Mostra come è calcolato lo Score"
        >
          <ScoreBadge value={row.score} label="Score" />
        </button>
      )}
      {certBadge}
    </span>
  );
}

/** Comparatore per la colonna "Peso" (peso della skill sulla competenza). */
function makeWeightComparator(weightOf: (skillId: string) => number) {
  return (
    a: AssessmentView,
    b: AssessmentView,
    direction: SortDirection,
  ): number => {
    const sign = direction === 'desc' ? -1 : 1;
    return sign * (weightOf(a.skillId) - weightOf(b.skillId));
  };
}

/**
 * Colonne in sola lettura sul modello Livello: Skill + Peso + Livello +
 * Esperienza + Score_Skill derivato. Nessuna colonna "Azioni" né picker di
 * modifica.
 *
 * A differenza di `TeamMemberCompetenceDetail`, la colonna "Livello" legge il
 * Livello AI (`aiLevel`): per un profilo esterno è l'unica Fonte di Livello
 * (coerente con quanto mostra `ExternalCvDetail`). Le colonne
 * Propensione/Reviews sono omesse: per un profilo esterno sarebbero sempre
 * vuote (campi `null` negli assessment esterni).
 */
function buildColumns(
  weightOf: (skillId: string) => number,
  descriptionBySkillId?: Map<string, string | undefined>,
  onScoreClick?: (row: AssessmentView) => void,
): Column<AssessmentView>[] {
  return [
    {
      key: 'skillName',
      header: 'Skill',
      sortable: true,
      sortComparator: compareSkillName,
      render: (row) => (
        <span className="skill-list__skill-cell">
          <span className="skill-list__skill-name">{row.skillName}</span>
          <InfoHint
            text={descriptionBySkillId?.get(row.skillId)}
            label={`Descrizione di ${row.skillName}`}
          />
        </span>
      ),
    },
    {
      key: 'weight',
      header: 'Peso',
      sortable: true,
      sortComparator: makeWeightComparator(weightOf),
      render: (row) => {
        const weight = weightOf(row.skillId);
        return (
          <DotRating
            value={weight}
            onChange={() => {}}
            readOnly
            size="sm"
            max={MAX_SKILL_WEIGHT}
            label={`Peso di ${row.skillName}`}
            optionLabel={(n) => `${n} — ${WEIGHT_HINT[n] ?? ''}`.trim()}
          />
        );
      },
      className: 'skill-list__score-col',
    },
    {
      key: 'aiLevel',
      header: (
        <span className="score-calc-guide__header">
          Livello
          <LivelloGuide className="score-calc-guide__trigger" />
        </span>
      ),
      sortable: true,
      sortComparator: compareAiLevel,
      render: (row) => (
        <span className="skill-list__level-cell">
          <LivelloBadge livello={row.aiLevel} />
        </span>
      ),
      className: 'skill-list__level-col',
    },
    {
      key: 'experience',
      header: 'Esperienza',
      sortable: true,
      sortComparator: compareExperience,
      render: (row) => experienceCell(row.yearsOfExperience),
      className: 'skill-list__experience-col',
    },
    {
      key: 'score',
      header: (
        <span className="score-calc-guide__header">
          Score
          <ScoreCalcGuide />
        </span>
      ),
      sortable: true,
      sortComparator: compareScore,
      render: (row) => scoreBadgeCell(row, onScoreClick),
      className: 'skill-list__score-col',
    },
  ];
}

export default function ExternalCvCompetenceDetail() {
  const api = useApiClient();
  const navigate = useNavigate();
  const { id, competenceId } = useParams<{
    id: string;
    competenceId: string;
  }>();

  // Dettaglio arricchito del profilo esterno: da qui gli `assessments` e i
  // `CompetencyMatch`. Un profilo inesistente (404) o un accesso negato (403)
  // non vanno ritentati.
  const dashboardQuery = useQuery<ExternalCvDashboardDto>({
    queryKey: ['externalCv', 'profiles', 'dashboard', id],
    queryFn: () =>
      api.get<ExternalCvDashboardDto>(
        `/api/external-cv/profiles/${encodeURIComponent(id ?? '')}/dashboard`,
      ),
    enabled: id !== undefined && id !== '',
    retry: false,
  });

  const competencesQuery = useQuery<CompetenceDto[]>({
    queryKey: ['competences'],
    queryFn: () => api.get<CompetenceDto[]>('/api/competences'),
  });

  const skillsQuery = useQuery<SkillDto[]>({
    queryKey: ['skills'],
    queryFn: () => api.get<SkillDto[]>('/api/skills'),
  });

  // Configurazione di scoring: alimenta il dettaglio del calcolo dello Score
  // (apribile cliccando il badge Score). In attesa/errore ripiega sui default.
  const scoringConfigQuery = useQuery({
    queryKey: ['settings', 'scoring'],
    queryFn: () => api.get<ScoringConfigDto>('/api/settings/scoring'),
    staleTime: 5 * 60 * 1000,
  });
  const scoringConfig = scoringConfigQuery.data ?? DEFAULT_SCORING_CONFIG;

  // Riga di cui è aperto il dettaglio di calcolo dello Score (o `null`).
  const [scoreDetailRow, setScoreDetailRow] = useState<AssessmentView | null>(null);

  const competence = useMemo(
    () => (competencesQuery.data ?? []).find((c) => c.id === competenceId) ?? null,
    [competencesQuery.data, competenceId],
  );

  const match = useMemo<CompetencyMatch | null>(
    () =>
      (dashboardQuery.data?.dashboard.competencyMatches ?? []).find(
        (m) => m.competenceId === competenceId,
      ) ?? null,
    [dashboardQuery.data, competenceId],
  );

  // Righe della tabella: le skill della competenza (nell'ordine di `skillIds`),
  // arricchite con gli assessment del profilo quando presenti; altrimenti una
  // riga "non valutata". Nessuna skill fuori dalla competenza.
  const rows = useMemo<AssessmentView[]>(() => {
    if (!competence) return [];
    const skillById = new Map((skillsQuery.data ?? []).map((s) => [s.id, s]));
    const assessmentBySkill = new Map(
      (dashboardQuery.data?.assessments ?? []).map((a) => [a.skillId, a]),
    );
    return competence.skillIds
      .map((skillId) => {
        const existing = assessmentBySkill.get(skillId);
        if (existing) return existing;
        const skill = skillById.get(skillId);
        if (!skill) return null;
        return {
          skillId,
          skillName: skill.name,
          selfInterest: null,
          aiLevel: null,
          reviewerLevelRank: null,
          reviewerCount: 0,
          reviewerUniformLevel: null,
          managerLevelRank: null,
          managerCount: 0,
          managerUniformLevel: null,
          // Profilo esterno in sola lettura: nessuna autovalutazione.
          livelloSelf: null,
          yearsOfExperience: null,
          score: null,
          // Skill non valutata: nessuna certificazione contribuisce allo Score.
          certificationCount: 0,
          // Skill non valutata: nessuna revisione.
          reviews: [],
        } satisfies AssessmentView;
      })
      .filter((r): r is AssessmentView => r !== null);
  }, [competence, skillsQuery.data, dashboardQuery.data]);

  // Peso (1..5) di una skill sulla competenza corrente; default 1 se assente.
  const skillWeights = competence?.skillWeights;
  const weightOf = useMemo(() => {
    const map = skillWeights ?? {};
    return (skillId: string) => map[skillId] ?? DEFAULT_SKILL_WEIGHT;
  }, [skillWeights]);

  const descriptionBySkillId = useMemo(() => {
    const map = new Map<string, string | undefined>();
    for (const s of skillsQuery.data ?? []) map.set(s.id, s.description);
    return map;
  }, [skillsQuery.data]);

  const columns = useMemo(
    () => buildColumns(weightOf, descriptionBySkillId, (row) => setScoreDetailRow(row)),
    [weightOf, descriptionBySkillId],
  );

  const backToProfile = () => {
    void navigate(`/external-cv/${encodeURIComponent(id ?? '')}`);
  };

  // ----- Rendering -----

  if (dashboardQuery.isLoading || competencesQuery.isLoading) {
    return (
      <section aria-labelledby="external-cv-competence-title">
        <h1 id="external-cv-competence-title">Competenza</h1>
        <Loading>Caricamento della competenza…</Loading>
      </section>
    );
  }

  // Il dettaglio arricchito (profilo) è la fonte autorevole per l'esistenza e
  // l'accesso: `404` (profilo inesistente) e `403` (accesso negato) sono gestiti
  // con un messaggio dedicato, coerentemente con `ExternalCvDetail`.
  if (dashboardQuery.isError || dashboardQuery.data === undefined) {
    const err = dashboardQuery.error;
    const notFound = err instanceof ApiRequestError && err.kind === 'not_found';
    const forbidden = err instanceof ApiRequestError && err.kind === 'forbidden';
    const message = notFound
      ? 'Il profilo richiesto non esiste o è stato eliminato.'
      : forbidden
        ? 'Non hai i permessi per consultare questo profilo.'
        : err instanceof Error
          ? err.message
          : 'Impossibile caricare il profilo.';
    return (
      <section aria-labelledby="external-cv-competence-title">
        <h1 id="external-cv-competence-title">Competenza</h1>
        <Alert tone={notFound || forbidden ? 'warning' : 'error'}>{message}</Alert>
        <div className="ui-toolbar" style={{ marginTop: 'var(--space-3)' }}>
          <Button type="button" onClick={backToProfile}>
            ← Torna al profilo
          </Button>
        </div>
      </section>
    );
  }

  if (competencesQuery.isError) {
    const message =
      competencesQuery.error instanceof Error
        ? competencesQuery.error.message
        : 'Impossibile caricare la competenza.';
    return (
      <section aria-labelledby="external-cv-competence-title">
        <h1 id="external-cv-competence-title">Competenza</h1>
        <Alert tone="error">{message}</Alert>
        <div className="ui-toolbar" style={{ marginTop: 'var(--space-3)' }}>
          <Button type="button" onClick={backToProfile}>
            ← Torna al profilo
          </Button>
        </div>
      </section>
    );
  }

  if (competence === null) {
    return (
      <section aria-labelledby="external-cv-competence-title">
        <h1 id="external-cv-competence-title">Competenza</h1>
        <EmptyState role="status">
          La competenza richiesta non esiste o non è più disponibile.
        </EmptyState>
        <div className="ui-toolbar" style={{ marginTop: 'var(--space-3)' }}>
          <Button type="button" onClick={backToProfile}>
            ← Torna al profilo
          </Button>
        </div>
      </section>
    );
  }

  const { firstName, lastName } = dashboardQuery.data.profile;
  const candidateName = `${firstName} ${lastName}`.trim();
  const subtitle = candidateName
    ? `Skill della competenza per ${candidateName}, derivate dalle valutazioni AI. Sola lettura.`
    : 'Skill della competenza derivate dalle valutazioni AI. Sola lettura.';

  return (
    <section className="competence-detail" aria-labelledby="external-cv-competence-title">
      <PageHeader
        title="Dettaglio competenza"
        titleId="external-cv-competence-title"
        subtitle={subtitle}
        actions={
          <Button type="button" variant="ghost" onClick={backToProfile}>
            ← Torna al profilo
          </Button>
        }
      />

      {/* Intestazione della competenza selezionata; a destra il donut della
          Copertura (stesso valore della card del dettaglio profilo). Accanto al
          nome la sola Proficiency, coerente con la dashboard personale. */}
      <div className="competence-picker__selected">
        <div>
          <span className="competence-picker__selected-label">
            {match ? (
              <>
                Proficiency{' '}
                {match.proficiencyPercent === null
                  ? '—'
                  : formatPercent(match.proficiencyPercent)}
              </>
            ) : (
              'Competenza'
            )}
          </span>
          <h2 className="competence-picker__selected-name">{competence.name}</h2>
        </div>
        {match && (
          <div className="competence-detail__score">
            <ScoreGuide className="competence-detail__score-guide" />
            <ScoreDonut score={match.coveragePercent} label="Copertura" />
          </div>
        )}
      </div>

      {/* Descrizione e mission affiancate, sotto la card del nome. Ogni card
          compare solo se il rispettivo campo è valorizzato. */}
      {(competence.description || competence.mission) && (
        <div className="competence-detail__info">
          {competence.description && (
            <Card className="competence-detail__info-card">
              <h3 className="competence-detail__info-title">Descrizione</h3>
              <p className="competence-detail__info-text">{competence.description}</p>
            </Card>
          )}
          {competence.mission && (
            <Card className="competence-detail__info-card">
              <h3 className="competence-detail__info-title">Mission</h3>
              <p className="competence-detail__info-text">{competence.mission}</p>
            </Card>
          )}
        </div>
      )}

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.skillId}
        loading={skillsQuery.isLoading}
        error={skillsQuery.isError ? 'Impossibile caricare le skill della competenza.' : null}
        emptyMessage="Questa competenza non ha skill collegate."
        defaultSort={{ key: 'skillName', direction: 'asc' }}
        paginated={false}
      />

      {/* Dettaglio del calcolo dello Score della skill selezionata, aperto dal
          badge Score cliccabile. Vista interna autenticata; il modale usa
          `createPortal`, quindi la posizione nel JSX non influisce sul layout. */}
      {scoreDetailRow !== null && (
        <ScoreDetailModal
          row={scoreDetailRow}
          config={scoringConfig}
          onClose={() => setScoreDetailRow(null)}
        />
      )}
    </section>
  );
}
